import React, { Component, Fragment } from 'react';
import Loading from '../../Loading'
import FilterBar from '../../Common/FilterBar';
import extensionAPI from '../../../flowtrace-api/extensionAPI';
import meetingAPI from '../../../flowtrace-api/meetingAPI';
import TrendComponent from './trendComponent';
import HistogramComponent from './histogramComponent';
import AttributeCard from './attributeCard';
import DatePicker from './pilotDateSelector';
import moment from 'moment';
import { Cache } from 'aws-amplify';
import { Grid } from '@material-ui/core';

class MeetingPilot extends Component {
  constructor(props) {
    super(props);
    const pilot = Cache.getItem("pilot") ? Cache.getItem("pilot") : moment().subtract(30, "days").format("YYYY-MM-DD");
    this.state = { loading: true, searchWarning:null, series:{}, histogram:{}, pilot, queryFilters: { range_period:"week"} };
  }

  async loadMeetingStatistics(queryFilters) {
    this.setState({ searchWarning:null});

    
    const pilotDate = queryFilters && queryFilters.pilot ? queryFilters.pilot : this.state.pilot;
    const isPilotDateOnlyChange = queryFilters && queryFilters.pilot && Object.keys(queryFilters).length === 1;
    const effectiveQueryFilters = isPilotDateOnlyChange ? { ...this.state.queryFilters, ...queryFilters } : (queryFilters || this.state.queryFilters);
    const prePilotQF = {
      ...effectiveQueryFilters,
      pilot: true,
      range_start: moment(pilotDate).subtract(30, "days").format("YYYY-MM-DD"),
      range_end: moment(pilotDate).subtract(1, "days").format("YYYY-MM-DD"),
      range_limit: effectiveQueryFilters.range_period === "day" ? 30 : 4
    }
    const postPilotQF = {
      ...effectiveQueryFilters,
      pilot: true,
      range_start: pilotDate,
      range_end: moment(pilotDate).add(1, "years").format("YYYY-MM-DD"),
      range_limit: undefined
    }

    const [preExtension, postExtension, preOverview, postOverview, distribution] = await Promise.all([
      extensionAPI.usage(prePilotQF, this.props.flowtraceUser),
      extensionAPI.usage(postPilotQF, this.props.flowtraceUser),
      meetingAPI.overview(prePilotQF, this.props.flowtraceUser),
      meetingAPI.overview(postPilotQF, this.props.flowtraceUser),
      meetingAPI.distribution(postPilotQF, this.props.flowtraceUser), // This creates two sets of data automatically
    ]);

    const getOverviewSeries = (name, object, invitePercentage = false) => {
      
      const getValue = (b, path) => {
        const pathParts = path.split(".");
        if(pathParts.length === 1) {
          return b[pathParts[0]] ? b[pathParts[0]] : null;
        }
        return getValue(b[pathParts[0]], pathParts.slice(1).join("."));
      }
      const convertToInvitesPercentage = (b, path) => {
        const invites = getValue(b, "invite.invites.avg");
        if(!invites) return null;
        const datapoint = getValue(b, path);
        if(!datapoint) return null;
        return datapoint / invites;
      }
      const getValueFunction = invitePercentage ? convertToInvitesPercentage : getValue;
      return [
        {name: name + " (after)", data: postOverview.histogram.map(b => [b.key, getValueFunction(b, object)])},
        {name: name + " (baseline)", data: preOverview.histogram.map(b => [b.key, getValueFunction(b, object)])},
      ];
    }
    const bDays = 30;
    const pDays = moment().diff(moment(pilotDate), "days");
    const dataSet = {
      series: {
        meetings: getOverviewSeries("Meetings", "event_count"),
        duration: getOverviewSeries("Duration", "duration.event.avg"),
        agenda: getOverviewSeries("Agenda", "agenda.average"),
        invites: getOverviewSeries("Invites", "invite.invites.avg"),
        optional: getOverviewSeries("Optional", "invite.optional.avg", true),
        accepted: getOverviewSeries("Accepted", "invite.accepted.avg", true),
        declined: getOverviewSeries("Declined", "invite.declined.avg", true),
        tentative: getOverviewSeries("Tentative", "invite.tentative.avg", true),
        pending: getOverviewSeries("Pending", "invite.pending.avg", true),
        call: getOverviewSeries("Call time", "video.call.avg"),
        late: getOverviewSeries("Call late", "video.late.avg"),
        callers: getOverviewSeries("Call participants", "video.participants.avg"),
        extension: []
      },
      histogram:{},
      total:{
        baseline: {
          duration: 0,
          invites: 0,
          meetings: 0,
          title:"Baseline",
          days: bDays,
          icon: "battery-empty"
        },
        pilot: {
          duration: 0,
          invites: 0,
          meetings: 0,
          title:"Pilot",
          days: pDays,
          icon: "battery-full"
        }
      },
      extension: []
    };
    dataSet.series.extension.push( {name: "Extension Users (baseline)", data: preExtension.histogram.map(b => [b.data, b.source.reduce((acc, cur) => acc += cur.users, 0)])})
    dataSet.series.extension.push( {name: "Extension Users (after)", data: postExtension.histogram.map(b => [b.data, b.source.reduce((acc, cur) => acc += cur.users, 0)])})

    preOverview.histogram.forEach(bucket => {
      dataSet.total.baseline.duration += bucket.duration.event.sum;
      dataSet.total.baseline.meetings += bucket.event_count;
      dataSet.total.baseline.invites += bucket.invite.invites.sum;
    })
    postOverview.histogram.forEach(bucket => {
      dataSet.total.pilot.duration += bucket.duration.event.sum;
      dataSet.total.pilot.meetings += bucket.event_count;
      dataSet.total.pilot.invites += bucket.invite.invites.sum;
    })

    dataSet.total.pilot.meetingsPerDay = dataSet.total.pilot.meetings / pDays;
    dataSet.total.baseline.meetingsPerDay = dataSet.total.baseline.meetings / bDays;

    const capitalizeFirstLetter = (val) => {
        return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }
    Object.keys(distribution).forEach(key => {
      if(!Array.isArray(distribution[key])) return;
      const previousTotal = distribution[key].reduce((acc, cur) => acc += cur.previous, 0);
      const currentTotal = distribution[key].reduce((acc, cur) => acc += cur.count, 0);
      let adjustX = (x) => x;
      if(key === "notice") adjustX = (x) => x + 24;
      if(key === "agenda") adjustX = (x) => x + 100;
      let adjustSeriesName = (s) => s;
      if(key === "invitesLow") adjustSeriesName = (s) => "Invites";
      if(key === "customInvites") adjustSeriesName = (s) => "Participants";
      if(key === "customDuration") adjustSeriesName = (s) => "Duration";
      if(key === "customDurationAll") adjustSeriesName = (s) => "Meeting Load";
      if(key === "customAgenda") adjustSeriesName = (s) => "Agenda Spread";

      dataSet.histogram[key] = [
        { name: capitalizeFirstLetter(adjustSeriesName(key)) + " (after)", data:distribution[key].map(b => ({x:adjustX(b.b), y: b.count / currentTotal, z: b.count, change: b.count / currentTotal - b.previous / previousTotal}) )},
        { name: capitalizeFirstLetter(adjustSeriesName(key)) + " (baseline)", data:distribution[key].map(b => ({x:adjustX(b.b), y: b.previous / previousTotal, z: b.previous}) )},
      ]
    })
    return this.setState({loading: false, ...dataSet});
  }

  getPilotDatePicker = () => {
    return  <DatePicker showTitle name="Deployment Date:" onChange={this.onPilotDateChange.bind(this)} selected={this.state.pilot} />;
  }

  cachePilotDate = (date) => {
    const expires = moment().add(1, "years").valueOf();
    Cache.setItem("pilot", date, { expires });
  }
  onPilotDateChange = (date) => {
    this.setState({loading: true, pilot:date});
    this.cachePilotDate(date);
    this.loadMeetingStatistics({pilot:date});
  }

  onChange(queryFilters) {
    this.setState({ loading: true, queryFilters });
    this.loadMeetingStatistics(queryFilters);
  }

  componentDidMount() {
    this.loadMeetingStatistics(this.state.queryFilters);
  }

  componentWillUnmount() { this.setState = () => { return; }; }

  render() {
    if(this.state.loading)
      return (
        <Fragment>
          <FilterBar teamFilter meetingFilter loading={this.state.loading} flowtraceUser={ this.props.flowtraceUser } queryFilters={{...this.state.queryFilters }} onChange={this.onChange.bind(this)} />
          <Loading text={"This feature requires Google or Outlook integration. Please inquire your account manager for more details."}/>
        </Fragment>);


    const formatWithOneDecimal = (val) => { 
      if(!val) return "";
      if(val === Math.round(val)) return val.toFixed(0);
      return val ? (val).toFixed(1) : ""; 
    };
    const formatPercentage = (val) => { 
      if(!val) return "0%";
      if(val <0.2) return (Math.round(val * 1000) / 10) + "%"; // Anything below 20% should have one decimal
      return (val * 100).toFixed(0) + "%"; 
    };

    const durationForCustom = (val) => { 
      if(!val) return ""
      return durationFromMinutes(val.split(".0")[0]);
    };
    const invitesForCustom = (val) => { 
      if(!val) return ""
      return val.replace(/\.0/g, '');
    };
    const durationFromHours = (val) => { 
      if(!val) return "00:00:00"
      return durationFromSeconds(val * 60 * 60);
    };
    const durationFromMinutes = (val) => { 
      if(!val) return "00:00:00"
      return durationFromSeconds(val * 60);
    };
    const durationFromSeconds = (val) => { 
      if(!val) return "00:00:00"
      const hours =  Math.floor(val / 60 / 60);
      const minutes =  Math.floor((val / 60) % 60);
      const seconds =  Math.round(val % 60);
      return `${hours > 9 ? hours : "0" +hours}:${minutes>9 ? minutes : "0" + minutes}:${seconds>9 ? seconds : "0" + seconds}`
    };
    const hoursToDay = (val) => { 
      if(!val) return "0d"
      if(val === 24) return "< 1d"
      return Math.floor(val / 24) + "d";
    };
    const isoDayToWeekday = (val) => { 
      return ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday", "Sunday", "Unknown"][val ? val - 1 : 7] || '';
    };
    const agendaFormatter = (val) => val === 100 ? `< ${val}` : val;
    const inviteGroupFormatter = (val) => `${val}-${val+9}`
    const pilotAnnotation = {
        xaxis: [
          {
            x: moment(this.state.pilot).subtract(4, "days").valueOf(),
            x2: moment(this.state.pilot).add(4, "days").valueOf(),
            fillColor: '#B3F7CA',
            label: {
              text: ''
            }
          },
          {
            x: moment(this.state.pilot).valueOf(),
            borderColor: '#775DD0',
            label: {
              text: 'Deployment',
              orientation: 'horizontal',
            }
          }
        ]
    }
    return (
      <Fragment>
        <FilterBar teamFilter meetingFilter loading={this.state.loading} flowtraceUser={ this.props.flowtraceUser } queryFilters={{...this.state.queryFilters }} onChange={this.onChange.bind(this)} injectedComponent={this.getPilotDatePicker()}/>
        <Grid container spacing={3}>
          <Grid item xs={6} md={3}>
            <AttributeCard values={this.state.total.baseline} title="Baseline" background="bg-warning"/>
          </Grid>
          <Grid item xs={6} md={3}>
            <AttributeCard values={this.state.total.pilot} baseline={this.state.total.baseline} title="Since Deployment" background="bg-dark"/>
          </Grid>
          <Grid item xs={12} md={6}>
            <AttributeCard type="roi" values={this.state.total.pilot} baseline={this.state.total.baseline} title="Estimated Savings" flowtraceUser={this.props.flowtraceUser}/>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className=" font-weight-bold font-size-xl text-uppercase" >Meeting Characteristics</div>
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Meetings" tooltip="Average of meeting events at the given time window." annotations={pilotAnnotation} series={this.state.series.meetings}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Duration" tooltip="Average duration of meeting events in minutes at the given time window." annotations={pilotAnnotation} formatter={durationFromMinutes} series={this.state.series.duration}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Agenda" tooltip="Average agenda length in characters per meeting at the given time window." annotations={pilotAnnotation} series={this.state.series.agenda}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Extension Usage" tooltip="Unique users of the Flowtrace extension at the given time window." annotations={pilotAnnotation} series={this.state.series.extension}  />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className=" font-weight-bold font-size-xl text-uppercase" >Meeting Invites and Response Rates</div>
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Invites" tooltip="Average invitees per meeting at the given time window." annotations={pilotAnnotation} formatter={formatWithOneDecimal} series={this.state.series.invites}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Accepted" tooltip="Average accepted invites per meeting at the given time window." annotations={pilotAnnotation} formatter={formatPercentage} series={this.state.series.accepted}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Optional" tooltip="Average optional invites per meeting at the given time window." annotations={pilotAnnotation} formatter={formatPercentage} series={this.state.series.optional}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Pending" tooltip="Average pending invites per meeting at the given time window." annotations={pilotAnnotation} formatter={formatPercentage} series={this.state.series.pending}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Declined" tooltip="Average declined invites per meeting at the given time window." annotations={pilotAnnotation} formatter={formatPercentage} series={this.state.series.declined}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Tentative" tooltip="Average tentative invites per meeting at the given time window." annotations={pilotAnnotation} formatter={formatPercentage} series={this.state.series.tentative}  />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className=" font-weight-bold font-size-xl text-uppercase" >Meeting Characteristics in Histograms</div>
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Duration" tooltip="" xformatter={durationForCustom} stepSize={20} series={this.state.histogram.customDuration} />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Participants" tooltip="" xformatter={invitesForCustom} series={this.state.histogram.customInvites}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Meeting Load" tooltip="" xformatter={invitesForCustom} series={this.state.histogram.customDurationAll}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Agenda Distribution" tooltip="" xformatter={invitesForCustom} series={this.state.histogram.customAgenda}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Duration" tooltip="Frequency of meeting event duration in minutes before, and after pilot deployment." xformatter={durationFromMinutes} stepSize={20} series={this.state.histogram.duration} />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Notice" tooltip="Meeting invite to event notice in hours before, and after pilot deployment." xformatter={hoursToDay} series={this.state.histogram.notice} max={480}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Participants 2-10" tooltip="Frequency of meeting invitees + organizer before, and after pilot deployment." xmin={2} series={this.state.histogram.invitesLow} max={120}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Participants 10+" tooltip="Frequency of meeting invitees in 10 participant steps before, and after pilot deployment." xformatter={inviteGroupFormatter } stepSize={2} series={this.state.histogram.invites} max={200}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Agenda Length" tooltip="Frequency of agenda length in characters before, and after pilot deployment." xformatter={agendaFormatter} series={this.state.histogram.agenda} max={2000}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Hour" tooltip="Frequency of meeting by event start time before, and after pilot deployment. Uses your current timezone." xformatter={durationFromHours} stepSize={4} series={this.state.histogram.hour} max={120}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistogramComponent title="Weekday" tooltip="Frequency of meeting by event weekday before, and after pilot deployment. Uses your current timezone." xformatter={isoDayToWeekday} series={this.state.histogram.weekday} max={120}  />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className=" font-weight-bold font-size-xl text-uppercase" >Meeting Video Call Information</div>
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Call Time" tooltip="Average duration of video call in seconds at the given time window." annotations={pilotAnnotation} formatter={durationFromSeconds} series={this.state.series.call}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Call participants" tooltip="Average video call participants per meeting at the given time window." annotations={pilotAnnotation} formatter={formatWithOneDecimal} series={this.state.series.callers}  />
          </Grid>
          <Grid item xs={12} md={6}>
            <TrendComponent title="Call late" tooltip="Average meeting delay in seconds at the given time window." annotations={pilotAnnotation} formatter={durationFromSeconds} series={this.state.series.late}  />
          </Grid>
        </Grid>
      </Fragment>
    );
  } // end of render()
} // end of class

export default MeetingPilot
